import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Google OAuth callback (our own Google OAuth client).
 *
 * Google redirects the browser back to the fixed redirect_uri
 * `https://sleepstack.nl/auth/callback?code=xxx` after a successful sign-in.
 * We read the `code` from the query string, post it to the backend for the
 * authorization-code exchange, and then swap it out of the URL for a clean
 * history entry before navigating to the dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
    const location = useLocation();
    const navigate = useNavigate();
    const { login } = useAuth();
    const { lang } = useLanguage();
    const [status, setStatus] = useState('verifying');
    const processed = useRef(false);

    useEffect(() => {
        // Guard against React StrictMode double-invocation of the effect.
        if (processed.current) return;
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        if (!code) {
            setStatus('error');
            return;
        }
        processed.current = true;

        // Clean the code out of the URL right away so a refresh cannot
        // replay it (and so it never ends up in analytics/referrers).
        window.history.replaceState(null, '', window.location.pathname);

        axios
            .post(`${API}/auth/google`, { code })
            .then((res) => {
                login(res.data.access_token, res.data.user);
                setStatus('success');
                setTimeout(() => navigate('/dashboard'), 900);
            })
            .catch(() => {
                setStatus('error');
            });
    }, [location.search, login, navigate]);

    const copy = {
        verifying: {
            nl: { title: 'Even je login afronden...', body: 'Een moment geduld.' },
            en: { title: 'Finishing your sign-in...', body: 'One moment please.' },
        },
        success: {
            nl: { title: 'Ingelogd!', body: 'Je gaat door naar je dashboard...' },
            en: { title: 'Signed in!', body: 'Redirecting to your dashboard...' },
        },
        error: {
            nl: { title: 'Login mislukt', body: 'De link is verlopen of ongeldig. Probeer opnieuw in te loggen.' },
            en: { title: 'Sign-in failed', body: 'The link expired or was invalid. Please try signing in again.' },
        },
    };
    const c = copy[status][lang === 'nl' ? 'nl' : 'en'];

    return (
        <div className="min-h-screen bg-accent flex items-center justify-center px-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="card-base p-10 max-w-sm w-full text-center"
                data-testid="auth-callback"
            >
                {status === 'verifying' && (
                    <>
                        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
                        <h2 className="heading-sm text-foreground mb-2">{c.title}</h2>
                        <p className="text-muted-foreground text-sm">{c.body}</p>
                    </>
                )}
                {status === 'success' && (
                    <>
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-2xl">✓</span>
                        </div>
                        <h2 className="heading-sm text-foreground mb-2">{c.title}</h2>
                        <p className="text-muted-foreground text-sm">{c.body}</p>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-2xl">✗</span>
                        </div>
                        <h2 className="heading-sm text-foreground mb-2">{c.title}</h2>
                        <p className="text-muted-foreground text-sm mb-6">{c.body}</p>
                        <a href="/login" className="btn-secondary text-sm px-6 py-2.5" data-testid="auth-callback-retry">
                            {lang === 'nl' ? 'Terug naar inloggen' : 'Back to sign-in'}
                        </a>
                    </>
                )}
            </motion.div>
        </div>
    );
}
